import { useCallback, useEffect, useState } from 'react';
import { api, type Passkey, type Session } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { addPasskey, describePasskeyError } from '../lib/passkey';

export function Security() {
  const { state, refresh } = useAuth();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([
      api.get<{ passkeys: Passkey[] }>('/api/auth/passkeys'),
      api.get<{ sessions: Session[] }>('/api/auth/sessions'),
    ]);
    setPasskeys(p.passkeys);
    setSessions(s.sessions);
  }, []);

  useEffect(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Load failed.'));
  }, [load]);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(describePasskeyError(err));
    } finally {
      setBusy(false);
    }
  }

  const remaining = state.status === 'signedIn' ? state.me.remainingRecoveryCodes : 0;

  return (
    <main className="page">
      <h1>Security</h1>
      {error && <p className="error">{error}</p>}

      <section className="panel">
        <h2>Passkeys</h2>
        <p className="hint">
          Register one on every device you use. With only one, losing that device means falling
          back to a code.
        </p>
        <ul className="list">
          {passkeys.map((p) => (
            <li key={p.id}>
              <div>
                <strong>{p.deviceName}</strong>
                <span className="meta">
                  added {formatDate(p.createdAt)}
                  {p.lastUsedAt ? ` · last used ${formatDate(p.lastUsedAt)}` : ' · never used'}
                </span>
              </div>
              <button
                className="danger"
                disabled={busy || passkeys.length <= 1}
                title={passkeys.length <= 1 ? 'Add another passkey before removing this one' : ''}
                onClick={() => void run(async () => { await api.del(`/api/auth/passkeys/${p.id}`); })}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <button
          className="primary"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const name = window.prompt('Name this device', 'My laptop');
              if (name) await addPasskey(name);
            })
          }
        >
          Add a passkey
        </button>
      </section>

      <section className="panel">
        <h2>Signed-in devices</h2>
        <ul className="list">
          {sessions.map((s) => (
            <li key={s.id}>
              <div>
                <strong>
                  {describeAgent(s.userAgent)}
                  {s.current && <span className="badge">this device</span>}
                </strong>
                <span className="meta">
                  {s.ip ?? 'unknown IP'} · last seen {formatDate(s.lastSeenAt)}
                </span>
              </div>
              {!s.current && (
                <button
                  className="danger"
                  disabled={busy}
                  onClick={() => void run(async () => { await api.del(`/api/auth/sessions/${s.id}`); })}
                >
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <h2>Recovery codes</h2>
        <p className="hint">{remaining} unused {remaining === 1 ? 'code' : 'codes'} remaining.</p>
        {newCodes ? (
          <>
            <ul className="codes">
              {newCodes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <p className="hint">Saved? These will not be shown again.</p>
            <button className="secondary" onClick={() => setNewCodes(null)}>
              Done
            </button>
          </>
        ) : (
          <button
            className="secondary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                if (!window.confirm('Generate new codes? Your existing codes stop working.')) return;
                const r = await api.post<{ recoveryCodes: string[] }>(
                  '/api/auth/recovery/regenerate',
                );
                setNewCodes(r.recoveryCodes);
                await refresh();
              })
            }
          >
            Generate new codes
          </button>
        )}
      </section>
    </main>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** The full UA string is noise on a phone; show the recognisable part. */
function describeAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Browser';
}
